/**
 * Orchestration du joueur DAG (machine à états thin : le SERVEUR décide du
 * nœud suivant / du déclenchement du chemin développé ; le client suit).
 *
 * La transition est PURE (`advancePlayer`) → testable sans React. Le hook câble
 * React Query (lecture + start + submit + complete) autour.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useCompleteDagAttempt,
  useDagForPlayer,
  useStartDagAttempt,
  useSubmitDagAnswer,
} from './api';
import {
  isPlayerDag,
  type CompleteAttemptResult,
  type PlayerChoix,
  type PlayerDag,
  type PlayerNoeud,
  type SubmitAnswerResult,
} from './types';

export interface PlayerStep {
  currentId: string;
  finished: boolean;
}

/**
 * Transition PURE : à partir du nœud courant et du verdict serveur, dit où aller.
 *   - correct + pas de nœud suivant ⇒ parcours terminé ;
 *   - un next_noeud_id ⇒ on avance (principal ou sous-nœud) ;
 *   - un subnode_to_play ⇒ on entre dans le chemin développé ;
 *   - sinon (faux sans chemin développé) ⇒ on réessaie le même nœud.
 */
export function advancePlayer(currentId: string, result: SubmitAnswerResult): PlayerStep {
  if (result.correct && result.next_noeud_id === null) return { currentId, finished: true };
  if (result.next_noeud_id) return { currentId: result.next_noeud_id, finished: false };
  if (result.subnode_to_play) return { currentId: result.subnode_to_play, finished: false };
  return { currentId, finished: false };
}

export interface AnswerLogEntry {
  noeudId: string;
  isSub: boolean;
  correct: boolean;
  scoreDelta: number;
}

export type PlayerStatus = 'loading' | 'error' | 'playing' | 'completed';

export interface DagPlayerState {
  status: PlayerStatus;
  /** Code d'erreur de chargement (access_denied / no_dag / …). */
  loadError: string | null;
  dag: PlayerDag | null;
  currentNode: PlayerNoeud | null;
  /** Choix du nœud courant (déjà mélangés côté serveur). */
  currentChoix: PlayerChoix[];
  /** Verdict de la dernière réponse (panneau de feedback) ; null = en attente. */
  lastResult: SubmitAnswerResult | null;
  score: number;
  answers: AnswerLogEntry[];
  completion: CompleteAttemptResult | null;
  busy: boolean;
  submitting: boolean;
  /** Soumet une réponse (choix + indices pêchés + demande d'aide éventuelle). */
  submit: (args: { choixId: string | null; fished: string[]; requestSubnode: boolean }) => void;
  /** Avance après lecture du feedback (Continuer / chemin développé). */
  next: () => void;
  /** Réessaie le nœud courant (efface le feedback sans changer de nœud). */
  retry: () => void;
}

function firstMainNode(dag: PlayerDag): PlayerNoeud | null {
  const mains = dag.noeuds.filter((n) => !n.is_sub).sort((a, b) => a.ordre - b.ordre);
  return mains[0] ?? null;
}

export function useDagPlayer(exerciseId: string): DagPlayerState {
  const query = useDagForPlayer(exerciseId);
  const startAttempt = useStartDagAttempt();
  const submitAnswer = useSubmitDagAnswer();
  const completeAttempt = useCompleteDagAttempt();

  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<SubmitAnswerResult | null>(null);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState<AnswerLogEntry[]>([]);
  const [completion, setCompletion] = useState<CompleteAttemptResult | null>(null);
  const startedRef = useRef(false);

  const dag = isPlayerDag(query.data) ? query.data : null;
  const loadError =
    query.data && !isPlayerDag(query.data)
      ? ((query.data as { error?: string }).error ?? 'unknown')
      : query.error
        ? 'load_failed'
        : null;

  // Démarrage : une fois le DAG chargé, on crée la tentative et on pointe le 1er nœud.
  useEffect(() => {
    if (!dag || startedRef.current) return;
    startedRef.current = true;
    setCurrentId(firstMainNode(dag)?.id ?? null);
    startAttempt.mutate(exerciseId, {
      onSuccess: (res) => {
        if (res.attempt_id) setAttemptId(res.attempt_id);
      },
    });
  }, [dag, exerciseId, startAttempt]);

  const nodeById = useMemo(
    () => new Map((dag?.noeuds ?? []).map((n) => [n.id, n] as const)),
    [dag],
  );
  const currentNode = currentId ? (nodeById.get(currentId) ?? null) : null;
  const currentChoix = useMemo(
    () => (dag && currentId ? dag.choix.filter((c) => c.noeud_id === currentId) : []),
    [dag, currentId],
  );

  const submit = useCallback(
    ({
      choixId,
      fished,
      requestSubnode,
    }: {
      choixId: string | null;
      fished: string[];
      requestSubnode: boolean;
    }) => {
      if (!attemptId || !currentNode || lastResult) return;
      submitAnswer.mutate(
        {
          attempt_id: attemptId,
          noeud_id: currentNode.id,
          choix_id: choixId,
          indices_peches: fished,
          request_subnode: requestSubnode,
        },
        {
          onSuccess: (res) => {
            setScore((s) => s + res.score_delta);
            setAnswers((a) => [
              ...a,
              {
                noeudId: currentNode.id,
                isSub: currentNode.is_sub,
                correct: res.correct,
                scoreDelta: res.score_delta,
              },
            ]);
            setLastResult(res);
          },
        },
      );
    },
    [attemptId, currentNode, lastResult, submitAnswer],
  );

  const next = useCallback(() => {
    if (!lastResult || !currentId) return;
    const step = advancePlayer(currentId, lastResult);
    setLastResult(null);
    if (step.finished) {
      if (attemptId) {
        completeAttempt.mutate(attemptId, { onSuccess: (res) => setCompletion(res) });
      } else {
        setCompletion({ ok: true, total_score: score, status: 'completed' });
      }
      return;
    }
    setCurrentId(step.currentId);
  }, [lastResult, currentId, attemptId, completeAttempt, score]);

  const retry = useCallback(() => setLastResult(null), []);

  let status: PlayerStatus = 'playing';
  if (query.isLoading) status = 'loading';
  else if (loadError) status = 'error';
  else if (completion) status = 'completed';

  return {
    status,
    loadError,
    dag,
    currentNode,
    currentChoix,
    lastResult,
    score,
    answers,
    completion,
    busy: startAttempt.isPending || completeAttempt.isPending,
    submitting: submitAnswer.isPending,
    submit,
    next,
    retry,
  };
}
